/** Structured save pipeline logging for diagnosing GitHub write failures. */

import { copyTextToClipboard } from './formatListClipboard';
import { escapeHtml } from './escapeHtml';
import type { DatabaseFile } from '../types';

export type SaveDebugEntry = {
  id: string;
  timestamp: number;
  runId: string | null;
  phase: string;
  path: string;
  ok: boolean;
  message: string;
  durationMs?: number;
  details?: Record<string, unknown>;
  error?: string;
};

const MAX_ENTRIES = 200;
const entries: SaveDebugEntry[] = [];
const listeners = new Set<() => void>();

let activeRunId: string | null = null;
let activeRunPath: string | null = null;
let activeRunStarted = 0;
let nextId = 1;

let modalEl: HTMLElement | null = null;
let listEl: HTMLElement | null = null;
let selectedId: string | null = null;

function notify(): void {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getActiveSaveRunId(): string | null {
  return activeRunId;
}

export function beginSaveRun(path: string): string {
  activeRunId = crypto.randomUUID();
  activeRunPath = path;
  activeRunStarted = Date.now();
  logSaveDebug({
    phase: 'run-start',
    path,
    ok: true,
    message: 'Save run started',
  });
  return activeRunId;
}

export function endSaveRun(ok: boolean, err?: unknown): void {
  if (!activeRunId) return;
  const path = activeRunPath ?? '(unknown)';
  const durationMs = Math.round(performance.now() - activeRunStarted);
  if (ok) {
    logSaveDebug({
      phase: 'run-complete',
      path,
      ok: true,
      message: 'Save run finished',
      durationMs,
    });
  } else {
    const error = err instanceof Error ? err.message : err != null ? String(err) : 'Save failed';
    logSaveDebug({
      phase: 'run-failed',
      path,
      ok: false,
      message: error,
      error,
      durationMs,
    });
  }
  activeRunId = null;
  activeRunPath = null;
  activeRunStarted = 0;
}

export function logSaveDebug(
  partial: Omit<SaveDebugEntry, 'id' | 'timestamp' | 'runId'> & {
    id?: string;
    timestamp?: number;
    runId?: string | null;
  }
): void {
  const entry: SaveDebugEntry = {
    id: partial.id ?? `s${nextId++}`,
    timestamp: partial.timestamp ?? Date.now(),
    runId: partial.runId !== undefined ? partial.runId : activeRunId,
    phase: partial.phase,
    path: partial.path,
    ok: partial.ok,
    message: partial.message,
    ...(partial.durationMs !== undefined ? { durationMs: partial.durationMs } : {}),
    ...(partial.details ? { details: partial.details } : {}),
    ...(partial.error ? { error: partial.error } : {}),
  };
  entries.unshift(entry);
  if (entries.length > MAX_ENTRIES) entries.length = MAX_ENTRIES;

  const detailStr = entry.details ? ` ${JSON.stringify(entry.details)}` : '';
  const durStr = entry.durationMs != null ? ` (${entry.durationMs}ms)` : '';
  const line = `[SaveDebug] ${entry.phase} ${entry.path} — ${entry.message}${durStr}${detailStr}`;
  if (entry.ok) console.info(line);
  else console.error(line, entry.error ?? '');

  notify();
}

export async function saveDebugStep<T>(
  phase: string,
  path: string,
  fn: () => Promise<T>,
  details?: Record<string, unknown>
): Promise<T> {
  const started = performance.now();
  logSaveDebug({
    phase: `${phase}-start`,
    path,
    ok: true,
    message: '…',
    ...(details ? { details } : {}),
  });
  try {
    const result = await fn();
    logSaveDebug({
      phase: `${phase}-done`,
      path,
      ok: true,
      message: 'ok',
      durationMs: Math.round(performance.now() - started),
      ...(details ? { details } : {}),
    });
    return result;
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    logSaveDebug({
      phase: `${phase}-fail`,
      path,
      ok: false,
      message: error,
      error,
      durationMs: Math.round(performance.now() - started),
      ...(details ? { details } : {}),
    });
    throw err;
  }
}

export function saveMediaCounts(file: DatabaseFile): Record<string, number> {
  let pendingImages = 0;
  let pendingAudio = 0;
  let pendingVideo = 0;
  let imagesToDelete = 0;
  let audioToDelete = 0;
  let videoToDelete = 0;
  let videoRenames = 0;
  for (const section of file.sections) {
    for (const item of section.items) {
      pendingImages += item.pendingImages.length;
      pendingAudio += item.pendingAudio.length;
      pendingVideo += item.pendingVideo.length;
      imagesToDelete += item.imagesToDelete.length;
      audioToDelete += item.audioToDelete.length;
      videoToDelete += item.videoToDelete.length;
      videoRenames += item.videoRenames?.length ?? 0;
    }
  }
  return {
    pendingImages,
    pendingAudio,
    pendingVideo,
    imagesToDelete,
    audioToDelete,
    videoToDelete,
    videoRenames,
    orphanedImages: file.orphanedImagePaths.length,
    orphanedAudio: file.orphanedAudioPaths.length,
    orphanedVideo: file.orphanedVideoPaths.length,
  };
}

export function getSaveDebugEntries(): readonly SaveDebugEntry[] {
  return entries;
}

export function clearSaveDebugLog(): void {
  entries.length = 0;
  selectedId = null;
  notify();
}

/** Short trace for save-failure alert dialogs. */
export function formatSaveFailureHint(err: unknown, maxLines = 10): string {
  const runId = entries.find(e => e.phase === 'run-failed' && !e.ok)?.runId ?? activeRunId;
  const relevant = entries
    .filter(e => (runId ? e.runId === runId : true))
    .slice(0, maxLines)
    .reverse();
  const lines = relevant.map(e => {
    const mark = e.ok ? 'ok' : 'FAIL';
    const dur = e.durationMs != null ? ` ${e.durationMs}ms` : '';
    return `${mark} ${e.phase}: ${e.message}${dur}`;
  });
  const errMsg = err instanceof Error ? err.message : String(err);
  const body = lines.length > 0 ? lines.join('\n') : errMsg;
  return `${body}\n\nFull trace: sidebar → Debug → Save log. HTTP log may show GitHub API bodies.`;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  } as Intl.DateTimeFormatOptions);
}

function formatEntryForClipboard(entry: SaveDebugEntry): string {
  return JSON.stringify(entry, null, 2);
}

function renderDetail(entry: SaveDebugEntry): string {
  const rows = [
    `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Path</span><pre class="http-debug-pre">${escapeHtml(entry.path)}</pre></div>`,
    `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Phase</span><pre class="http-debug-pre">${escapeHtml(entry.phase)}</pre></div>`,
    `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Message</span><pre class="http-debug-pre">${escapeHtml(entry.message)}</pre></div>`,
  ];
  if (entry.runId) {
    rows.push(
      `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Run</span><pre class="http-debug-pre">${escapeHtml(entry.runId)}</pre></div>`
    );
  }
  if (entry.details) {
    rows.push(
      `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Details</span><pre class="http-debug-pre">${escapeHtml(JSON.stringify(entry.details, null, 2))}</pre></div>`
    );
  }
  if (entry.error) {
    rows.push(
      `<div class="http-debug-detail-row"><span class="http-debug-detail-label">Error</span><pre class="http-debug-pre http-debug-status--error">${escapeHtml(entry.error)}</pre></div>`
    );
  }
  return rows.join('');
}

function renderList(): void {
  if (!listEl) return;
  if (entries.length === 0) {
    listEl.innerHTML = '<p class="http-debug-empty">No save attempts logged yet.</p>';
    return;
  }

  listEl.innerHTML = entries
    .map(entry => {
      const open = entry.id === selectedId;
      const statusClass = entry.ok ? 'http-debug-status--ok' : 'http-debug-status--error';
      const duration = entry.durationMs != null ? `${entry.durationMs}ms` : '—';
      return `
        <article class="http-debug-entry${open ? ' http-debug-entry--open' : ''}" data-id="${escapeHtml(entry.id)}">
          <div class="http-debug-entry-top">
            <button type="button" class="http-debug-entry-summary">
              <span class="http-debug-entry-time">${escapeHtml(formatTime(entry.timestamp))}</span>
              <span class="http-debug-entry-method">${escapeHtml(entry.phase)}</span>
              <span class="http-debug-entry-status ${statusClass}">${entry.ok ? 'ok' : 'fail'}</span>
              <span class="http-debug-entry-duration">${escapeHtml(duration)}</span>
              <span class="http-debug-entry-url">${escapeHtml(entry.path.split('/').pop() ?? entry.path)}</span>
            </button>
            <button type="button" class="http-debug-entry-copy btn-ghost-dark btn-sm" aria-label="Copy entry">Copy</button>
          </div>
          ${open ? `<div class="http-debug-entry-detail">${renderDetail(entry)}</div>` : ''}
        </article>
      `;
    })
    .join('');

  listEl.querySelectorAll<HTMLElement>('.http-debug-entry-summary').forEach(btn => {
    btn.addEventListener('click', () => {
      const row = btn.closest('.http-debug-entry');
      const id = row?.getAttribute('data-id') ?? null;
      selectedId = id && selectedId !== id ? id : null;
      renderList();
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('.http-debug-entry-copy').forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const id = btn.closest('.http-debug-entry')?.getAttribute('data-id');
      const entry = id ? entries.find(en => en.id === id) : undefined;
      if (!entry) return;
      const label = btn.textContent;
      void copyTextToClipboard(formatEntryForClipboard(entry)).then(
        () => {
          btn.textContent = 'Copied';
          window.setTimeout(() => {
            btn.textContent = label ?? 'Copy';
          }, 1500);
        },
        () => {
          btn.textContent = 'Failed';
          window.setTimeout(() => {
            btn.textContent = label ?? 'Copy';
          }, 1500);
        }
      );
    });
  });
}

function closeModal(): void {
  if (!modalEl) return;
  modalEl.classList.add('hidden');
  document.body.style.overflow = '';
}

function ensureModal(): HTMLElement {
  if (modalEl) return modalEl;

  const modal = document.createElement('div');
  modal.id = 'save-debug-modal';
  modal.className = 'http-debug-modal hidden';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Save debug log');
  modal.innerHTML = `
    <div class="http-debug-modal-backdrop" aria-hidden="true"></div>
    <div class="http-debug-modal-sheet">
      <header class="http-debug-modal-header">
        <h2 class="http-debug-modal-title">Save debug log</h2>
        <div class="http-debug-modal-actions">
          <button type="button" class="btn-ghost-dark btn-sm" id="save-debug-copy-all">Copy all</button>
          <button type="button" class="btn-ghost-dark btn-sm" id="save-debug-clear">Clear</button>
          <button type="button" class="btn-primary btn-sm" id="save-debug-close">Close</button>
        </div>
      </header>
      <p class="http-debug-modal-hint">Save pipeline steps (media sync, serialize, GitHub write). Also logged to the browser console as [SaveDebug]. Pair with HTTP log for API response bodies.</p>
      <div class="http-debug-list" id="save-debug-list"></div>
    </div>
  `;

  listEl = modal.querySelector('#save-debug-list');
  modal.querySelector('.http-debug-modal-backdrop')!.addEventListener('click', closeModal);
  modal.querySelector('#save-debug-close')!.addEventListener('click', closeModal);
  modal.querySelector('#save-debug-clear')!.addEventListener('click', () => {
    clearSaveDebugLog();
    renderList();
  });
  modal.querySelector('#save-debug-copy-all')!.addEventListener('click', () => {
    void copyTextToClipboard(JSON.stringify(entries, null, 2));
  });

  document.body.appendChild(modal);
  modalEl = modal;
  return modal;
}

export function mountSaveDebugModal(): void {
  ensureModal();
  subscribe(() => {
    if (modalEl && !modalEl.classList.contains('hidden')) renderList();
  });
}

export function openSaveDebugModal(): void {
  const modal = ensureModal();
  renderList();
  modal.classList.remove('hidden');
  document.body.style.overflow = 'hidden';
}
